import { JustSpinner } from 'components/Loading/Loading'
import React from 'react'
import styled from 'styled-components'
import { tokens } from 'styling/tokens'
import { EmptyList, EmptyListProps } from './EmptyList'

export enum Align {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export interface OptionsProps<T> {
  header: string
  cell: (item: T) => React.ReactNode
  align?: Align
  id?: keyof T
  onClick?: (item: T) => void
}

interface TableProps<T> {
  options: OptionsProps<T>[]
  // item must have an id
  items: T[]
  cellKey?: keyof T
  emptyListOptions?: EmptyListProps
  loading?: boolean
  onClick?: (item: T) => void
  isRowHighlighted?: (item: T) => boolean
}

export const Table = <T,>({
  options,
  items,
  cellKey,
  emptyListOptions,
  loading,
  onClick,
  isRowHighlighted,
}: TableProps<T>) => {
  return (
    <TableWrapper>
      <TableStyled>
        <thead>
          <tr>
            {options.map((option) => (
              <th key={option.header} align={option.align || Align.LEFT}>
                {option.header}
              </th>
            ))}
          </tr>
        </thead>
        {loading ? (
          <tr>
            <td colSpan={options.length}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                }}
              >
                <JustSpinner />
              </div>
            </td>
          </tr>
        ) : items.length > 0 ? (
          <tbody>
            {items.map((item) => (
              <tr
                key={item[cellKey] as string}
                onClick={() => onClick?.(item)}
                data-highlighted={isRowHighlighted?.(item) ? 'true' : undefined}
              >
                {options.map((option) => (
                  <td key={option.header} align={option.align || Align.LEFT}>
                    {option.cell(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : (
          <tr>
            <td colSpan={options.length}>
              <EmptyList {...emptyListOptions} />
            </td>
          </tr>
        )}
      </TableStyled>
    </TableWrapper>
  )
}

const TableStyled = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${tokens.font.body};
  color: ${tokens.text.primary};

  thead th {
    padding: 0.85rem 1.15rem;
    font-size: ${tokens.font.micro};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${tokens.text.muted};
    background: rgba(255, 255, 255, 0.025);
    border-bottom: 1px solid ${tokens.border.strong};
    white-space: nowrap;
  }

  tbody tr {
    cursor: pointer;
    transition: background ${tokens.motion.duration} ${tokens.motion.ease};
  }

  tbody tr:hover,
  tbody tr[data-highlighted='true'] {
    background: rgba(255, 255, 255, 0.045);
  }

  tbody td {
    padding: 0.9rem 1.15rem;
    border-bottom: 1px solid ${tokens.border.hairline};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`

const TableWrapper = styled.div`
  width: 100%;
  border-radius: ${tokens.radius.card};
  overflow-x: auto;
  overflow-y: hidden;
  background: ${tokens.surface.glass};
  backdrop-filter: blur(${tokens.blur});
  border: 1px solid ${tokens.border.hairline};
  box-shadow: ${tokens.shadow.card};
`
